/**
 * La entrada.
 *
 * Tres envoltorios y un enrutador, en este orden y no en otro:
 *
 * - **La barrera** va fuera del todo. Si un error de pintado revienta dentro, lo
 *   recoge; si estuviera dentro del enrutador, un fallo al montar una ruta dejaría
 *   la página en blanco sin nada que lo cuente. Existe por un fallo real —una fecha
 *   de Eden puesta suelta en un JSX— que apagaba el panel entero sin decir nada.
 * - **La caché** antes de la sesión, porque `ProveedorDeSesion` ya consulta.
 * - **La sesión** antes del enrutador: las rutas deciden a dónde ir según
 *   `can_open`, así que tiene que estar resuelta cuando la primera se monte.
 *
 * Qué aplicación se abre lo decide la ruta `/` (`shared/rutas`), con `can_open` de
 * `GET /me`: **viene resuelto del servidor y no se recalcula aquí** (`BFF_BO.md`
 * §5.1). Deducir permisos en el cliente es como se acaba enseñando un botón que da
 * 403, y con cuatro aplicaciones es como se acaba deduciendo distinto en cada una.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { enrutador } from '@/shared/rutas/rutas';
import { ProveedorDeSesion } from '@/shared/auth/sesionContexto';
import { Barrera } from '@/shared/ui/Barrera';
import { vigilarTablas } from '@/shared/ui/etiquetasDeTablas';
import { aplicacionDeEsteDominio, rutaEnOtroDominio } from '@/shared/dominios';
import '@/shared/ui/tema.css';

/*
 * En el dominio del back office, la pestaña lo dice desde el primer momento, también
 * en la pantalla de acceso: dos pestañas que se llaman igual son justo lo que
 * liaba a quien tiene las dos (02-11-2026). Dentro, el marco lo vuelve a poner.
 */
if (aplicacionDeEsteDominio() === 'backoffice') {
  document.title = 'Back office · Kiap';
}

/*
 * Lo pendiente se recuenta después de cualquier cambio: firmas el contrato y la
 * campana deja de contarlo sin esperar a su siguiente vuelta. Con segundo y medio de
 * respiro, para que pasar lista a veinte alumnos sea un recuento y no veinte.
 * Invalidar una consulta que no está montada no hace nada, así que en el panel se
 * recuenta lo del club y en el back office lo de Kiap.
 */
let recuento: ReturnType<typeof setTimeout> | undefined;
const mutaciones = new MutationCache({
  onSuccess: () => {
    clearTimeout(recuento);
    recuento = setTimeout(() => {
      void cache.invalidateQueries({ queryKey: ['hoy'] });
      void cache.invalidateQueries({ queryKey: ['admin', 'pendientes'] });
    }, 1500);
  },
});

const cache = new QueryClient({
  mutationCache: mutaciones,
  defaultOptions: {
    queries: {
      // El bucle de refresco ya reintenta lo que merece reintentarse, y un 403 o
      // un 404 no mejoran repitiéndolos: reintentar aquí además duplicaría las
      // entradas del registro de accesos del back office.
      retry: false,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const raiz = document.getElementById('raiz');
if (!raiz) throw new Error('Falta #raiz en index.html');

// Las tablas, en tarjetas en el móvil: cada celda lleva el nombre de su columna.
vigilarTablas();

/*
 * Una ruta de la otra aplicación (un marcador viejo `app.…/admin/clubes`) se va a su
 * dominio **sin montar nada**: montar arranca la sesión, que renueva el token, y
 * saltar con esa renovación en vuelo la corta (ver `rutaEnOtroDominio`).
 */
const otroDominio = rutaEnOtroDominio();

if (otroDominio) {
  window.location.replace(otroDominio);
} else {
  createRoot(raiz).render(
    <StrictMode>
      <Barrera>
        <QueryClientProvider client={cache}>
          <ProveedorDeSesion>
            <RouterProvider router={enrutador} />
          </ProveedorDeSesion>
        </QueryClientProvider>
      </Barrera>
    </StrictMode>
  );
}
